import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Max width of the content panel. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Whether clicking the backdrop closes the dialog. Defaults to true. */
  dismissOnBackdrop?: boolean;
  /** aria-label for the dialog. */
  label?: string;
}

const SIZES: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

export function Dialog({
  open,
  onClose,
  children,
  size = 'md',
  dismissOnBackdrop = true,
  label,
}: DialogProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Prevent scroll on the page while dialog is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
    >
      {/* Backdrop — frosted glass over a black scrim per Malsi design */}
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={() => dismissOnBackdrop && onClose()}
        className={cn(
          'absolute inset-0 z-0 cursor-default bg-black/60',
          'backdrop-blur-malsi',
        )}
      />
      <div
        className={cn(
          'relative z-10 w-full',
          SIZES[size],
          'rounded-2xl border border-surface-divider bg-surface-raised',
          'shadow-none',
        )}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close dialog"
          className={cn(
            'absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center',
            'rounded-full bg-brand-red text-fg',
            'hover:bg-brand-redAlt transition-colors',
          )}
        >
          <X size={16} strokeWidth={2} />
        </button>
        <div className="max-h-[85vh] overflow-y-auto p-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
